import AntIcon from '@expo/vector-icons/AntDesign';
import { Divider } from '@mui/material';
import { useIsFocused } from '@react-navigation/native';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, FlatList, ScrollView } from 'react-native';
import { TripProps } from 'src/common/api/response';
import { useRatingInfiniteQuery } from 'src/common/hooks';
import { isCloseToBottom, keyExtractor } from 'src/common/utils/KeyExtractor';
import { Center, Container, EmptyState, Operator, RegularText, Row, SizedBox } from 'src/components';
import { AppColors } from 'src/themes/Colors';
import { metrics, pixelScaler } from 'src/themes/Metrics';
// import { NewCustomDropdown } from 'src/components/bases/NewCustomDropdown';

// const sortByData = [
//   { label: 'Newest', value: '1' },
//   { label: 'Oldest', value: '2' },
//   { label: 'Hight Rating', value: '3' },
//   { label: 'Low Rating', value: '4' },
// ];

export const ReviewContent = ({ trip }: { trip?: TripProps }) => {
  const {
    data: ratingData,
    isLoading: ratingIsLoading,
    isFetchingNextPage: ratingIsFetchingNextPage,
    fetchNextPage: ratingFetchNextPage,
    hasNextPage: ratingHasNextPage,
    refetch: ratingRefetch,
    isFetching: ratingIsFetching,
    isError: ratingIsError,
    error: ratingError,
  } = useRatingInfiniteQuery({ operatorId: trip?.operator.id || 0 });

  // const [sortBy, setSortBy] = React.useState(sortByData[0]);
  const { t } = useTranslation();

  const focus = useIsFocused();

  useEffect(() => {
    console.log({ focus });
    if (focus) ratingRefetch();
  }, [focus]);

  const { ratings, totalReviews, avgRating } = React.useMemo(() => {
    if (ratingData?.pages) {
      return {
        ratings: ratingData.pages.map(p => p.data.data).flat(),
        totalReviews: get(ratingData, 'pages[0].data.totalReviews'),
        avgRating: get(ratingData, 'pages[0].data.avgRating'),
      };
    }
    return { ratings: [], totalReviews: 0, avgRating: 0 };
  }, [ratingData]);

  const renderSeperator = () => {
    return <Container style={{ borderBottomWidth: 1, borderColor: AppColors.border }} />;
  };

  const handleFetchMore = () => {
    if (ratingHasNextPage && !ratingIsFetching) ratingFetchNextPage();
  };

  if (ratingIsLoading || ratingIsError) {
    return <EmptyState loading={ratingIsLoading} error={ratingError} text="tripEmptyContent" />;
  }

  return (
    <ScrollView
      contentContainerStyle={{ padding: metrics.large }}
      onScroll={({ nativeEvent }) => {
        if (isCloseToBottom(nativeEvent)) {
          handleFetchMore();
        }
      }}
      scrollEventThrottle={16}>
      <Row style={{ justifyContent: 'space-between' }}>
        <Operator showRating={false} operator={trip?.operator} busService={trip?.service} />
        <RatingStar rating={avgRating || 0} reviewCount={totalReviews || 0} />
      </Row>
      {/* <Divider style={{ marginTop: metrics.large, marginBottom: metrics.large }} />
      <StarGenerator label="Safety" count={5} reviewCount={30} />
      <StarGenerator label="Cleanliness" count={4} reviewCount={5} />
      <StarGenerator label="Punctual" count={3} reviewCount={2} /> */}
      <Divider style={{ marginTop: metrics.large, marginBottom: metrics.large }} />

      <Row middle marginBottom={metrics.large}>
        <Container flex>
          <RegularText fontWeight="bold">{t('reviews')}</RegularText>
          <RegularText color={AppColors.gray}>{t('usersReviewed', { count: totalReviews })}</RegularText>
        </Container>
        {/* <SortDropdown label="sortBy" data={sortByData} value={sortBy} onChange={setSortBy} /> */}
      </Row>
      {/* <TextButton size="medium" text="viewAll" /> */}
      <FlatList
        // data={ratings}

        data={ratingIsFetching && !ratingIsFetchingNextPage ? [] : ratings}
        keyExtractor={keyExtractor}
        renderItem={ReviewerUser}
        ItemSeparatorComponent={renderSeperator}
        ListFooterComponent={
          <Center height={50}>{ratingIsFetchingNextPage ? <ActivityIndicator animating /> : null}</Center>
        }
      />
    </ScrollView>
  );
};

const generateStar = (count: number, activeCount: number) => {
  const stars = [];
  for (let i = 0; i < count; i += 1) {
    stars.push(
      <AntIcon
        key={i.toString()}
        style={{ marginRight: 1 }}
        color={i < activeCount ? AppColors.yellow : AppColors.lightGray}
        name="star"
        size={pixelScaler(13)}
      />,
    );
  }
  return <Row>{stars}</Row>;
};

// const StarGenerator = ({ count, label, reviewCount }: { count: number; label: string; reviewCount: number }) => {
//   return (
//     <Row height={pixelScaler(25)} style={{ alignItems: 'center' }}>
//       <Container flex>
//         <RegularText numberOfLines={1}>{label}</RegularText>
//       </Container>
//       <SizedBox />
//       {generateStar(5, count)}
//       <Container width={pixelScaler(80)} style={{ alignItems: 'flex-end' }}>
//         <RegularText color={AppColors.gray} fontSize={pixelScaler(12)}>
//           ({reviewCount} reviews)
//         </RegularText>
//       </Container>
//     </Row>
//   );
// };

const ReviewerUser = ({ item }: { item: RatingProps }) => {
  return (
    <Container width="100%" marginTop={metrics.medium}>
      {/* <Container style={{ borderBottomWidth: 1, borderColor: AppColors.border }} flex marginLeft={metrics.medium}> */}
      <Row flex style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Container>
          <RegularText>{item.displayName || '*******'}</RegularText>
          <SizedBox height="tiny" />
          {generateStar(item.rate, item.rate)}
        </Container>
        <Container flex>
          <RegularText color={AppColors.gray} textAlign="right">
            {item.createdAt}
          </RegularText>
        </Container>
      </Row>
      <Container paddingVertical={metrics.medium}>
        <RegularText>{item.comment}</RegularText>
      </Container>
      {/* </Container> */}
    </Container>
  );
};

const RatingStar = ({ rating, reviewCount }: { rating: number; reviewCount: number }) => {
  return (
    <Container align="center">
      <Row middle>
        <RegularText fontWeight="bold" fontSize="large">
          {rating}
        </RegularText>
        <AntIcon style={{ marginLeft: 2 }} color={AppColors.yellow} name="star" size={pixelScaler(13)} />
      </Row>
      <RegularText fontSize="medium" color={AppColors.gray}>
        by {reviewCount} users
      </RegularText>
    </Container>
  );
};
